/**
 * The filter control, and the sentence that admits it is on.
 *
 * # The sentence is the feature
 *
 * A filter left on is indistinguishable from mail having gone missing, and the
 * reader's next move is to conclude the sync is broken and go looking for a
 * fault that is not there. So whenever anything is on, the bar tints, gains a
 * left edge, and says in words what it is doing and how many messages that
 * leaves -- above the list, never scrolled away, with one button that undoes all
 * of it.
 *
 * It is also the answer to the sidebar. Inbox can say 12 unread while the list
 * shows 3 rows, and with nothing to explain it that reads as a counting bug;
 * the sentence names the mailbox it is filtering so the two numbers are
 * visibly answers to different questions.
 *
 * # Controls behind a dropdown, state in front of it
 *
 * This used to be a row of `aria-pressed` chips, on the argument that the state
 * *was* the control and so could not be hidden. The row cost too much for what
 * it bought: four chips plus a date chip do not fit on one line in a 320px list
 * column, so the strip was routinely two rows of controls tall above every
 * message list, filtered or not -- permanent furniture paying for a mode that is
 * off almost all of the time.
 *
 * The dropdown is only allowed because the argument the chips were defending is
 * satisfied somewhere better. Collapsing the *controls* is safe; collapsing the
 * *state* is the failure. So the trigger carries a count of what is on, and the
 * sentence -- the words, the match count, the one-click Clear -- stays out in
 * the open beside it, visible with the panel shut and outside `col-body` so it
 * cannot be scrolled away. There is no state in this feature whose only home is
 * behind a click. The date range moved inside the panel for the same reason its
 * chip used to carry its label: the sentence already spells the range out ("from
 * 3 Mar to 9 Mar"), so the editor is the only part that is hidden.
 *
 * A disclosure, not a menu: the toggles inside stay `aria-pressed` toggles that
 * combine, so the trigger gets `aria-expanded` and the contents get group
 * labels. A `<select>` was never possible -- several of these are on at once.
 *
 * # Applying is immediate, on a phone too
 *
 * No Apply button, even though a panel invites one. The list behind the panel is
 * the preview: a filter is chosen by seeing how much it leaves, and a deferred
 * Apply hides exactly that until it is too late to reconsider. It would also
 * introduce the one state this file exists to prevent -- a pending set that
 * disagrees with the visible one. Dismissing the panel therefore changes
 * nothing; the way out is Clear, which is one button and always on screen.
 */

import { useEffect, useRef, useState } from "preact/hooks";

import * as filters from "../lib/filters";
import * as store from "../lib/store";
import * as Icon from "./Icons";

/**
 * What the filters are being applied to, in words. Exported because the empty
 * state has to name the same place in the same words -- "No unread messages in
 * Inbox" and the bar's "in Inbox" describing two different scopes would be
 * worse than either alone.
 */
export function scopeLabel(): string {
  const s = store.scope.value;
  if (s.kind === "mailbox") {
    const name = store.mailboxes.value
      .get(s.accountId)
      ?.find((m) => m.id === s.mailboxId)?.name;
    return name ?? "this mailbox";
  }
  // Neither of these takes the "across N accounts" treatment a unified role
  // does, and not because it would be long. A unified Sent is a merge of the
  // accounts that happen to have a Sent folder, so the number is news; these
  // two span every account by construction, so it would only ever be the number
  // of accounts signed in -- which the reader can already see in the sidebar.
  // Saved could not say it in any case: `store.scopeAccounts` is
  // deliberately empty there, because that computed answers a question about
  // queries and this view is not one.
  if (s.kind === "starred") return store.STARRED_LABEL;
  if (s.kind === "saved") return store.KEPT_LABEL;
  if (s.kind === "reminders") return store.REMINDERS_LABEL;
  // Counted off the accounts the view actually spans, not off every account
  // signed in. A unified Sent over one account with a Sent folder and one
  // without is one account's worth of mail, and "across 2 accounts" would be
  // the client asserting it had looked somewhere it had not.
  //
  // Named as a merge only when it is one: "Inbox across 1 account" is a
  // sentence nobody with one account should ever have to read.
  const n = store.scopeAccounts.value.length;
  // A search widens a unified view from one role to every mailbox in every
  // account -- `specFor` drops the mailbox as soon as there is text -- so
  // saying "in Inbox" here would name a scope the query is not using.
  if (store.search.value.trim()) return n > 1 ? `all ${n} accounts` : "every mailbox";
  const role = s.role.charAt(0).toUpperCase() + s.role.slice(1);
  return n > 1 ? `${role} across ${n} accounts` : role;
}

function countLabel(): string {
  const n = store.matchTotal.value;
  if (n === undefined) return "";
  return n === 1 ? " — 1 match" : ` — ${n.toLocaleString()} matches`;
}

/**
 * The sentence, and only the sentence.
 *
 * The trigger used to live here too. It sits in the list header beside the
 * search box now, which is where someone looks for it — narrowing a list by
 * typing and narrowing it by ticking a box are the same intent, and they were
 * two rows apart.
 *
 * What is left is the *state*, and it stays a strip of its own rather than
 * following the trigger up into the header. Two reasons. The header is a fixed
 * row of controls and the sentence is prose of unpredictable length, so it
 * would either truncate — losing the half that says which filters are on — or
 * force the header to grow. And with the trigger gone this strip has nothing to
 * show when no filter is set, so it collapses to nothing: the row it costs is a
 * row that only exists while it has something to say.
 *
 * It collapses in CSS rather than by returning `null`, which is the tempting
 * version and is wrong for the same reason the note below is emptied rather
 * than unmounted — see there.
 */
export function FilterBar() {
  const f = filters.filters.value;
  const active = filters.anyActive.value;
  return (
    <div class="filter-bar" data-active={active ? "true" : "false"}>
      <div class="filter-head">
        {/*
          `role="status"` rather than a bare paragraph: this is the one thing on
          screen that explains a short list, and someone who cannot see the tint
          has to be told when it changes. Polite, so it waits for a gap rather
          than interrupting -- and it carries the count in the same sentence,
          because "filtered" without "and this is how much is left" is half an
          answer.

          Always in the DOM, emptied rather than removed, and hidden by
          `:empty`. A live region that is inserted at the same moment as its
          text is a race: some screen readers register the region and never see
          the content, which loses the announcement in the one direction that
          matters -- a filter going *on*.
        */}
        <p class="filter-note" role="status">
          {active && (
            <>
              <span>
                Showing only {filters.messagesPhrase(f)} in {scopeLabel()}
                {countLabel()}
              </span>
              <button class="filter-clear" onClick={() => store.clearFilters()}>
                Clear filters
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
}

/**
 * One funnel, and everything behind it.
 *
 * The count on the trigger is the part that earns the collapse: a dot would say
 * "something is on" and leave the reader to open the panel to find out how much,
 * and the number is the same pixels. It is deliberately the same number
 * `activeCount` gives the keyboard and the sentence, so the badge cannot drift
 * from what Clear would undo -- the date range counts once however many of its
 * ends are set, because it is one thing someone turned on.
 *
 * The `f` chords in `app.tsx` do not go through here at all. They call the store
 * directly, so `f u` works with this shut and stays a two-keystroke operation
 * rather than growing an open-the-panel step; the feedback is the sentence,
 * which is on screen either way.
 */
export function FilterMenu() {
  const f = filters.filters.value;
  const n = filters.activeCount.value;
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  // Dismissed by a click elsewhere, like every other popover anyone has used --
  // the same pattern the date editor used when it was its own popover, kept
  // rather than reinvented. On `pointerdown` rather than `click` so the panel is
  // gone before whatever was clicked reacts, which stops a row selecting and the
  // panel closing from looking like two separate frames.
  //
  // No focus restore on this path: the pointer has already chosen where
  // attention goes, and yanking focus back to the trigger would fight it.
  //
  // The listener alone is not enough, and the gap is the largest target on
  // screen. The message body is a sandboxed iframe in a browsing context of its
  // own (`ui/Reader.tsx`, `lib/html.ts`), so a pointerdown on the mail someone
  // is reading is delivered inside that document and never reaches this one --
  // no bubbling crosses the boundary, and with no `allow-same-origin` there is
  // nothing to attach a listener to on the other side. Nor does `onFocusOut`
  // below cover it: focus moving into a frame arrives with a `relatedTarget` of
  // null, which is exactly the case it has to ignore for Firefox's date picker.
  // So the panel sat open over the reader, dismissed by every click except the
  // obvious one.
  //
  // Focus is the one thing that does cross: clicking into the frame blurs this
  // window and leaves the frame itself as `activeElement`. That test is what
  // separates it from the window losing focus for any other reason -- switching
  // app or tab leaves `activeElement` where it was, and a filter panel that
  // evaporated every time someone alt-tabbed would be its own bug. Read on a
  // timeout because the order of `blur` against the `activeElement` update is
  // not something to depend on across browsers; by the next task it has
  // settled.
  useEffect(() => {
    if (!open) return;
    const away = (e: Event) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    let queued: number | undefined;
    const blurred = () => {
      queued = setTimeout(() => {
        if (document.activeElement instanceof HTMLIFrameElement) setOpen(false);
      }, 0);
    };
    document.addEventListener("pointerdown", away);
    window.addEventListener("blur", blurred);
    return () => {
      clearTimeout(queued);
      document.removeEventListener("pointerdown", away);
      window.removeEventListener("blur", blurred);
    };
  }, [open]);

  // Focus moves in on open, to the first toggle. Not a trap: Tab walks the three
  // toggles and the date fields and then leaves, and `onFocusOut` below shuts
  // the panel behind it, so a keyboard user is never held inside and never
  // leaves an orphan panel hanging over the list.
  useEffect(() => {
    if (!open) return;
    panel.current?.querySelector<HTMLElement>("button, input")?.focus();
  }, [open]);

  const close = (restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) trigger.current?.focus();
  };

  const label = n === 0 ? "Filter messages" : `Filter messages — ${n} on`;
  const dateOn = Boolean(f.since || f.until);

  return (
    // Focusable, though never a Tab stop, for the sake of macOS. WebKit there
    // does not focus a button that is clicked -- the platform's convention --
    // and hands focus to the nearest focusable ancestor instead. Without this
    // that ancestor is the list column (`tabIndex={-1}` for pane focus), so
    // every click on a toggle arrived at `onFocusOut` as focus leaving for the
    // list, and the panel shut under the pointer while Tab and Enter worked.
    // With it the click lands focus here, inside, and the panel stays. It also
    // makes the panel block the title-bar drag region it sits in, which treats
    // anything with a tabindex as a control.
    <div
      class="filter-menu"
      ref={wrap}
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key !== "Escape" || !open) return;
        // Stopped here so the document handler in `app.tsx` does not also read
        // this Escape as "clear the selection, then close the message". One
        // Escape, one thing undone, outermost first.
        e.stopPropagation();
        close(true);
      }}
      onFocusOut={(e) => {
        const next = e.relatedTarget as Node | null;
        // `null` means focus went nowhere this handler can reason about -- a
        // click on empty page, or a native date picker taking over. Left to the
        // pointerdown handler rather than guessed at, because closing on a
        // relatedTarget of null is what makes date inputs unusable in Firefox.
        if (next && !wrap.current?.contains(next)) setOpen(false);
      }}
    >
      <button
        class="filter-trigger"
        ref={trigger}
        data-active={n > 0 ? "true" : "false"}
        aria-expanded={open}
        aria-controls="filter-panel"
        aria-label={label}
        title={label}
        onClick={() => (open ? close(true) : setOpen(true))}
      >
        <Icon.Filter class="icon" />
        {/*
          Dropped by the stylesheet where the column is too narrow to spare it.
          The button keeps its `aria-label` and `title` either way, so what goes
          is nine characters of reassurance, not the name of the control -- and
          the funnel plus the count still say what it is and whether it is on.
        */}
        <span class="flabel">Filters</span>
        {n > 0 && (
          <span class="filter-count" aria-hidden="true">
            {n}
          </span>
        )}
        <Icon.Chevron class="icon fchev" />
      </button>

      {open && (
        <div class="filter-panel" id="filter-panel" ref={panel}>
          <div class="fpanel-group" role="group" aria-label="Show only">
            {filters.TOGGLES.map((t) => (
              <button
                key={t.key}
                class="fopt"
                aria-pressed={f[t.key]}
                aria-label={t.hint}
                onClick={() => store.toggleFilter(t.key)}
              >
                <span class="fopt-tick" aria-hidden="true">
                  <Icon.Check class="icon" />
                </span>
                {t.key === "flagged" ? (
                  <Icon.Star class="icon" />
                ) : t.key === "attachment" ? (
                  <Icon.Paperclip class="icon" />
                ) : (
                  <Icon.MailClosed class="icon" />
                )}
                <span>{t.label}</span>
              </button>
            ))}
          </div>

          <div class="fpanel-rule" />

          <DateRange dateOn={dateOn} />

          <div class="fpanel-foot">
            {/*
              Clear is here as well as in the sentence, because the panel is
              where someone who has just turned three things on is looking, and
              making them shut it to find the way out is the sort of thing that
              gets a filter left on. Disabled rather than absent so the footer
              does not resize as toggles go on and off under the cursor.
            */}
            <button class="btn" disabled={n === 0} onClick={() => store.clearFilters()}>
              Clear all
            </button>
            <button class="btn primary" onClick={() => close(true)}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The date range.
 *
 * Both ends are optional and mean what they read as: "since the 3rd" with no
 * upper end, "up to the 3rd" with no lower. Both inclusive -- the conversion to
 * JMAP's exclusive `before` happens in `lib/filters.ts`, once, rather than
 * being something each caller has to remember.
 *
 * The `min`/`max` cross-wiring stops the two inputs describing an empty range,
 * which the server would answer correctly and which would look exactly like a
 * broken filter.
 *
 * Two fields in the panel rather than a popover hanging off a chip. Nesting one
 * disclosure inside another is two Escapes, two click-outside handlers and a
 * panel that can open past the edge of a 320px column; the group heading carries
 * the range so nothing is lost by flattening it, and the sentence above the list
 * says it again in full.
 */
function DateRange({ dateOn }: { dateOn: boolean }) {
  const f = filters.filters.value;
  return (
    <div class="fpanel-group" role="group" aria-label="Date range">
      {/*
        The heading names the group *and* reads its value back -- "Date range: 5
        Aug – 9 Aug". Two `mm/dd/yyyy` boxes are the state in a form nobody reads
        at a glance, and the bare range on its own ("5 Aug – 9 Aug", floating
        above two inputs) does not say what it is a range of.
      */}
      <div class="fpanel-head">
        <span>Date range{dateOn ? `: ${filters.dateLabel(f)}` : ""}</span>
        <button
          class="filter-clear"
          disabled={!dateOn}
          onClick={() => store.setFilterDates({ since: "", until: "" })}
        >
          Reset
        </button>
      </div>
      <div class="fpanel-dates">
        <label class="date-field">
          <span>From</span>
          <input
            type="date"
            value={f.since}
            max={f.until || undefined}
            onChange={(e) => store.setFilterDates({ since: (e.target as HTMLInputElement).value })}
          />
        </label>
        <label class="date-field">
          <span>To</span>
          <input
            type="date"
            value={f.until}
            min={f.since || undefined}
            onChange={(e) => store.setFilterDates({ until: (e.target as HTMLInputElement).value })}
          />
        </label>
      </div>
    </div>
  );
}
